/**
 * Video-project feature flags (server + Next client).
 * Mirror VIDEO_PROJECT_* and NEXT_PUBLIC_VIDEO_PROJECT_* like TTS.
 */

function envFlagEnabled(
	serverKey: string,
	publicKey: string,
	defaultEnabled: boolean,
): boolean {
	const raw = (
		process.env[serverKey] ||
		process.env[publicKey] ||
		""
	)
		.trim()
		.toLowerCase();
	if (!raw) return defaultEnabled;
	return raw !== "false" && raw !== "0" && raw !== "off";
}

/**
 * When false, skip Veo / Modal Wan. Stock (Pexels/Wikimedia) still runs.
 * Env: VIDEO_PROJECT_ENABLE_AI_VIDEO / NEXT_PUBLIC_VIDEO_PROJECT_ENABLE_AI_VIDEO
 * Default: true
 */
export function isVideoProjectAiVideoEnabled(): boolean {
	return envFlagEnabled(
		"VIDEO_PROJECT_ENABLE_AI_VIDEO",
		"NEXT_PUBLIC_VIDEO_PROJECT_ENABLE_AI_VIDEO",
		true,
	);
}

export const VIDEO_PROJECT_VIDEO_PROVIDERS = {
	modal: "modal",
	veo: "veo",
} as const;

export type VideoProjectVideoProvider =
	(typeof VIDEO_PROJECT_VIDEO_PROVIDERS)[keyof typeof VIDEO_PROJECT_VIDEO_PROVIDERS];

/**
 * AI clip backend after stock miss.
 * Env: VIDEO_PROJECT_VIDEO_PROVIDER / NEXT_PUBLIC_VIDEO_PROJECT_VIDEO_PROVIDER
 * Default: modal (Wan 2.2). Set `veo` for Gemini Veo 3.1.
 */
export function getVideoProjectVideoProvider(): VideoProjectVideoProvider {
	const raw = (
		process.env.VIDEO_PROJECT_VIDEO_PROVIDER ||
		process.env.NEXT_PUBLIC_VIDEO_PROJECT_VIDEO_PROVIDER ||
		""
	)
		.trim()
		.toLowerCase();
	if (raw === VIDEO_PROJECT_VIDEO_PROVIDERS.veo) return "veo";
	return "modal";
}

export const VIDEO_PROJECT_TEXT_PROVIDERS = {
	deepseek: "deepseek",
	gemini: "gemini",
} as const;

export type VideoProjectTextProvider =
	(typeof VIDEO_PROJECT_TEXT_PROVIDERS)[keyof typeof VIDEO_PROJECT_TEXT_PROVIDERS];

/**
 * Parse an explicit DeepSeek / Gemini value. Blank or unknown → undefined
 * so callers can fall through to env.
 */
export function parseVideoProjectTextProvider(
	raw?: string | null,
): VideoProjectTextProvider | undefined {
	const value = raw?.trim().toLowerCase();
	if (value === VIDEO_PROJECT_TEXT_PROVIDERS.gemini) return "gemini";
	if (value === VIDEO_PROJECT_TEXT_PROVIDERS.deepseek) return "deepseek";
}

/**
 * Env default for bible / script / expand / validate / fix / critic / editorial / polish.
 * Image, Veo, TTS, stock vision, and Google-Search research stay on Gemini.
 * Env: VIDEO_PROJECT_TEXT_PROVIDER / NEXT_PUBLIC_VIDEO_PROJECT_TEXT_PROVIDER
 * Default: deepseek
 */
export function getVideoProjectTextProvider(): VideoProjectTextProvider {
	return (
		parseVideoProjectTextProvider(process.env.VIDEO_PROJECT_TEXT_PROVIDER) ??
		parseVideoProjectTextProvider(
			process.env.NEXT_PUBLIC_VIDEO_PROJECT_TEXT_PROVIDER,
		) ??
		"deepseek"
	);
}

/** Per-call override, then env default. */
export function coerceVideoProjectTextProvider(
	raw?: string | null,
): VideoProjectTextProvider {
	return parseVideoProjectTextProvider(raw) ?? getVideoProjectTextProvider();
}
